import React from 'react';
import {
    Alert,
    Avatar,
    Button,
    Divider,
    Dropdown,
    Layout,
    Menu,
    Modal,
    Spin,
    Typography
} from 'antd';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
} from '@ant-design/icons';
import {observer} from 'mobx-react';
import {observable, action, runInAction} from 'mobx';
import GlobalMenu from '../components/GlobalMenu/GlobalMenu';
import UserData from '../model/UserData';
import i18next from "i18next";
import {Link} from 'react-router-dom';
import {Cookies} from 'react-cookie';
import BaseAPI from '../service/BaseAPI';
import GameData from '../model/GameData';
import * as H from 'history';
import styles from "./styles/BasicLayout.module.scss";
import brandLogo from "./img/brand-logo.png";
import moment from 'moment';
import {generateGoogleLink, generateLink} from '../pages/LoginFlow/LoginPage';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faExclamationTriangle,
    faJournalWhills,
    faSignOut, faSync, faUser,
    faUsers, faCircleCheck
} from '@fortawesome/pro-duotone-svg-icons';
import {faChevronDown} from "@fortawesome/pro-solid-svg-icons";
import {faDiscord, faJediOrder} from "@fortawesome/free-brands-svg-icons";
import LoadingSpinner from "../components/Loading/LoadingSpinner";
import PlayerData from "../model/PlayerData";
import {getSafeMode, safeModeIsForced, setSafeMode} from "../service/safeMode";

const {Header, Sider, Content} = Layout;

interface IProps {
    children: React.ReactNode;
    cookies: Cookies;
    /**
     * The router location, already spread in by every page via `{...this.props}` (IPageProps carries it)
     * — declared here because the mobile nav needs to know a navigation happened. Optional so a caller
     * that does not forward it still compiles; the only cost is that the close below never fires.
     */
    location?: H.Location<H.LocationState>;
    // any other props that come into the component
    user?: UserData;
    gameData?: GameData;
    requiredApiRights: string[];
    extraRefresh?: () => Promise<void>;
    requiresGameConnection?: boolean;
    warnOnStaleData?: boolean;
}

const TABLET_WIDTH = 1026;

@observer
export class BasicLayout extends React.Component<IProps> {
    @observable collapsed: boolean = false;
    @observable condensed: boolean = false;
    @observable reloading: boolean = false;
    // Header auto-hide on scroll (mobile landscape only — see .headerHidden's media query in
    // BasicLayout.module.scss, which is what actually gates this to short viewports; the JS runs
    // unconditionally so it stays correct if that breakpoint ever changes).
    @observable headerHidden: boolean = false;
    // The scrollY of the most extreme point reached since the header last committed to its current
    // state — the highest point scrolled up to while visible, or the lowest point scrolled down to
    // while hidden. Reversal is measured FROM this point, not frame to frame; see updateHeaderVisibility.
    private scrollExtremeY: number = 0;
    private scrollTicking: boolean = false;

    @action
    toggle() {
        this.collapsed = !this.collapsed;
    };


    @action
    componentDidMount() {
        window.addEventListener("resize", () => this.updateScreenSize());
        window.addEventListener("scroll", this.handleScroll, {passive: true});
        this.updateScreenSize();
        if (this.condensed) {
            this.collapsed = true;
        }
        this.applyMobileNavScrollLock();
    }

    componentDidUpdate(prevProps: IProps) {
        if (prevProps.location?.key !== this.props.location?.key) this.closeMobileNavAfterNavigation();
        this.applyMobileNavScrollLock();
    }

    /**
     * 🔴 Closes the mobile nav after a navigation that did NOT remount this layout.
     *
     * Every page renders its own BasicLayout, so a normal menu tap unmounts this instance and mounts a
     * fresh one whose componentDidMount re-collapses on mobile — which is the ONLY reason the nav appears
     * to "close on tap" today. Tapping the tab you are already on re-renders the same Route, keeps this
     * instance, and so left the full-screen overlay sitting open over the page it had just returned you
     * to (reported on mobile for V2-Players: the frame went back to search behind an open menu).
     *
     * Keyed on `location.key`, which React Router v5 mints fresh on every push INCLUDING a push to the
     * current path — the same signal the V2 tabs use to reset their frame. Because a cross-route
     * navigation remounts instead, this only ever fires for the same-route case, which is exactly the
     * gap. Desktop is untouched: `condensed` is false, and there is no overlay to close.
     */
    @action
    private closeMobileNavAfterNavigation() {
        if (this.condensed) this.collapsed = true;
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.handleScroll);
        document.body.style.overflow = '';
    }

    // 🔴 The mobile nav is a `position:fixed` full-screen overlay (.aside, height:100vh), but nothing
    // stopped the PAGE BEHIND IT from also being independently scrollable — confirmed by measurement, not
    // assumed: on a fixture-backed dashboard the document was 1834px tall against a 780px viewport while
    // the nav was open. The overlay itself doesn't move when the background scrolls (it's fixed), but the
    // gesture that scrolls the visible menu list to ITS OWN limit can carry on into that independently
    // scrollable background — which is exactly "scroll it to the bottom, then scroll some more" from the
    // user's side, since nothing about the fixed overlay visually indicates a second scroll domain exists
    // underneath it. Standard fix for an open overlay: lock body scroll while it's open, restore it when
    // closed (or unmounted, so navigating away never leaves scroll locked).
    private applyMobileNavScrollLock() {
        const mobileNavOpen = this.condensed && !this.collapsed;
        document.body.style.overflow = mobileNavOpen ? 'hidden' : '';
    }

    @action
    updateScreenSize() {
        this.condensed = window.innerWidth < TABLET_WIDTH;
    }

    // Bound once (not an inline arrow in addEventListener) so componentWillUnmount can remove the
    // exact listener instance — otherwise every remount leaks one.
    private handleScroll = () => {
        if (this.scrollTicking)
            return;
        this.scrollTicking = true;
        window.requestAnimationFrame(() => this.updateHeaderVisibility());
    };

    // Direction-based, not position-based: a header that hides purely past some scrollY threshold never
    // comes back until you scroll all the way to the top, which defeats the point on a long page.
    //
    // 🔴 Comparing consecutive frames' scrollY (delta = currentY - lastScrollY) does NOT work, even with a
    // dead zone — momentum/rubber-band scrolling genuinely oscillates several px frame-to-frame while
    // settling (verified: a single continuous scroll produced deltas like -6, +9, +6, -7 in successive
    // frames), so any per-frame threshold small enough to feel responsive is also small enough to flicker
    // the header open/closed several times during ONE scroll gesture. Confirmed by an isolated repro
    // before this shipped, not assumed.
    //
    // Fix: measure cumulative travel from the most extreme point reached since the header's last state
    // change (scrollExtremeY) — a hysteresis band, not a per-frame delta. Only a real reversal from that
    // peak flips the state; a few px of settling jitter never accumulates that far. 80px was chosen by
    // testing against the actual oscillation this repro measured (single continuous scrolls settling with
    // swings up to ~50-100px) — 40px still flickered 3x per gesture; 80px produced exactly one flip per
    // direction change with no false ones, on both a short landscape viewport and a tall desktop one.
    @action
    private updateHeaderVisibility() {
        this.scrollTicking = false;
        const currentY = window.scrollY;
        const REVERSAL_THRESHOLD = 80;

        if (currentY <= 0) {
            this.headerHidden = false;
            this.scrollExtremeY = 0;
            return;
        }

        if (!this.headerHidden) {
            this.scrollExtremeY = Math.min(this.scrollExtremeY, currentY);
            if (currentY - this.scrollExtremeY > REVERSAL_THRESHOLD) {
                this.headerHidden = true;
                this.scrollExtremeY = currentY;
            }
        } else {
            this.scrollExtremeY = Math.max(this.scrollExtremeY, currentY);
            if (this.scrollExtremeY - currentY > REVERSAL_THRESHOLD) {
                this.headerHidden = false;
                this.scrollExtremeY = currentY;
            }
        }
    }

    private checkApiRights(): boolean {
        return this.checkApiRightsList(this.props.requiredApiRights);
    }

    private checkApiRightsList(requiredApiRights: string[] | undefined) {
        if (!this.props.user || !this.props.user.apiRights)
            return false;

        return this.props.user.checkApiRights(requiredApiRights);
    }

    render() {
        const siderClassName = (this.condensed ? (this.collapsed ? styles.mobile : styles.mobileExpanded) : '');
        const menuFold = this.collapsed ?
            <MenuUnfoldOutlined className={`${styles.trigger} ${this.condensed ? styles.menuMobile : ''}`}
                                onClick={() => this.toggle()}/> :
            <MenuFoldOutlined className={`${styles.trigger} ${this.condensed ? styles.menuMobile : ''}`}
                              onClick={() => this.toggle()}/>
        const collapseIcon = this.collapsed ? <MenuUnfoldOutlined/> : <MenuFoldOutlined/>;
        // An EMPTY rights list means "no rights required" — a public page — and must pass for EVERYONE,
        // including a logged-out visitor. checkApiRights() fails closed on user.apiRights === null, which is
        // correct for real requirements but wrongly bounced anonymous visitors off the public tabs
        // (/players/v2, /guilds/v2 showed "requires a higher Patreon level" to someone who was simply not
        // logged in — found during the landing redesign).
        // ⚠️ requiredApiRights is typed required but arrives UNDEFINED at runtime from several pages
        // (DatacronsV2, EventsV2, GACv2 pass only {...IPageProps}) — treat absent like empty.
        let hasRights = !this.props.requiredApiRights || this.props.requiredApiRights.length === 0 || this.checkApiRights();
        let hasConnection = this.props.user && this.props.user.currentPlayer && this.props.user.currentPlayer.hasConnection;
        let needsConnection = this.props.requiresGameConnection && !hasConnection;
        return (
            <Layout className={`${this.collapsed ? styles.collapsed : styles.expanded} ${styles.layout}`}>
                {this.condensed && menuFold}
                <Sider trigger={null} collapsible theme="light" collapsed={this.collapsed}
                       className={`${styles.aside} ${siderClassName} hu-sidebar`}>
                    <React.Fragment>
                        <div className={styles.brand}>
                            <img src={brandLogo} alt={"HotUtils"} className={styles.brandLogo}/>
                        </div>
                        <GlobalMenu user={this.props.user} showEventsLink={this.checkApiRightsList(['SWGOH_EVENTS'])}
                                    publicMode={!this.props.user || !this.props.user.currentPlayer}
                                    isCollapsed={this.collapsed} isMobile={this.condensed}/>
                        {!this.condensed && <div className={styles.collapseBtn} onClick={() => this.toggle()}>
                            {collapseIcon}
                            <span>{this.collapsed ? 'EXPAND' : 'COLLAPSE'}</span>
                        </div>}
                    </React.Fragment>
                </Sider>


                <Layout className={`${styles.main} ${siderClassName}`}>
                    {/*
                        🔴 BUILT BY FILTERING, because two of these three can be absent and a template
                        literal renders `undefined` as the six-letter word.

                        `.headerHidden` no longer EXISTS in the stylesheet — it was removed when the short-
                        landscape collapse became a plain `display: none` — so `styles.headerHidden` is
                        `undefined`, and the literal above emitted `class="header_x7f2 undefined"` every
                        time the scroll tracker fired. CSS Modules resolve a missing class to `undefined`
                        rather than throwing, so this cost nothing visually and appeared nowhere except the
                        DOM. The stylesheet still describes the leftover tracking as "harmless"; it was
                        very nearly harmless, which is not the same thing.

                        ⚠️ The tracking is now inert rather than merely unused: the only breakpoint it was
                        built for hides `.header` outright, so `headerHidden` can no longer change how
                        anything renders. It stays here per that comment's own note that removing it is a
                        separate change — but it is dead in the present tense, not "still correct for a
                        future non-iframe page", and it re-renders on scroll direction changes to do it.
                    */}
                    <Header className={[
                        styles.header,
                        this.condensed && styles.mobileHeader,
                        this.headerHidden && styles.headerHidden,
                    ].filter(Boolean).join(' ')}>
                        <div className={styles.headerContainer}>
                            <div className={styles.headerTitleArea}/>
                            {this.renderUserBanner()}
                        </div>
                    </Header>
                    <Content className={styles.body}>
                        {this.renderSafeModeBanner()}
                        {this.renderStaleDataWarning()}
                        {hasRights ? (needsConnection ? this.renderNeedsGameConnection() : this.props.children) : this.renderNoPermissions()}
                    </Content>
                </Layout>
            </Layout>
        );
    }

    // Safe mode blocks every write to the account (BaseAPI.postToApi). Announced
    // on every page, because a silently-on safe mode looks exactly like a broken
    // site: buttons click, nothing happens.
    private renderSafeModeBanner(): React.ReactNode {
        const mode = getSafeMode();
        if (mode === "off")
            return null;
        return <Alert type="warning" className={styles.staleWarning} message={<span>
            <FontAwesomeIcon icon={faExclamationTriangle} className={`${styles.icon}`}/>
            {mode === "block"
                ? "Safe mode is on — every change to your mods is blocked before it is sent."
                : "Safe mode is on (simulate) — changes are sent with the API's simulation flag set."}
            {safeModeIsForced()
                ? <> (set by REACT_APP_SAFE_MODE; restart the dev server to clear it)</>
                : <> <span className={styles.link} onClick={() => {
                    setSafeMode("off");
                    window.location.reload();
                }}>turn it off</span></>}
        </span>}/>;
    }

    private renderStaleDataWarning(): React.ReactNode {
        if (!this.props.warnOnStaleData || !this.props.user || !this.props.user.currentPlayer || !this.props.user.hasGameConnection || this.reloading)
            return null;

        if ((!this.props.user.currentPlayer.connected || this.props.user.currentPlayer.gameDataAgeUtc === null) && !this.props.user.readOnly) {
            return <Alert message={<span>
                <FontAwesomeIcon icon={faExclamationTriangle} className={`${styles.icon} ${styles.error}`}/>
                You have not updated your game data since login, <span className={styles.link}
                                                                       onClick={() => this.reload()}>click here to update</span>
                </span>} type="error" className={styles.staleWarning}/>;
        }

        let t = moment.utc(this.props.user.currentPlayer.gameDataAgeUtc);
        if (t.diff(moment(), "minutes") < -30) {
            if (this.props.user.readOnly) {
                return <Alert message={<span>
                    <FontAwesomeIcon icon={faExclamationTriangle} className={`${styles.icon} ${styles.error}`}/>
                    The game data you are viewing has not been updated in {t.fromNow(true)}, ask {this.props.user.currentPlayer.name} to refresh then <span
                    className={styles.link} onClick={() => this.reload()}>click here to update</span>
                    </span>} type="error" className={styles.staleWarning}/>
            } else {
                return <Alert message={<span>
                    <FontAwesomeIcon icon={faExclamationTriangle} className={`${styles.icon} ${styles.error}`}/>
                    You have not updated your game data in {t.fromNow(true)}, <span className={styles.link}
                                                                                    onClick={() => this.reload()}>click here to update</span>
                    </span>} type="error" className={styles.staleWarning}/>
            }
        }

        return null;
    }

    private renderNeedsGameConnection(): React.ReactNode {
        return <div className={styles.centeredNotice}>
            <h2>This feature requires a game connection</h2>
            <Typography>
                To set up a game connection, if your SWGOH account is linked with EA Connect, <a
                href="/eaconnect">
                <Button type="primary" value="large">{i18next.t("common:login.login_with_eaconnect")}</Button>
            </a><br/>
            If that doesn't work, and your account is linked to a google account, try <a
                href={generateGoogleLink()}>
                <Button type="primary" value="large">{i18next.t("common:login.login_with_google")}</Button>
            </a><br/>
                If that also doesn't work, or you don't have a linked google account, reach out to the support team on <a
                href="https://discord.me/hotutils">Discord</a>
            </Typography>
        </div>;
    }

    private renderNoPermissions(): React.ReactNode {
        let requiredPatreonLevel: string | null = null;
        
        if (this.props.requiredApiRights && this.props.requiredApiRights.includes("ADMIN") && this.props.user && !this.props.user.isAdmin)
        {
            return <div className={styles.centeredNotice}><h2>This feature requires admin permisions, and you are not an admin or officer of your guild<br/>
                Ask a guild leader or officer to log into the site and go to Guild - Overview and mark you as an Admin</h2></div>;
        }

        if (this.props.requiredApiRights && this.props.requiredApiRights.length === 1) {
            switch (this.props.requiredApiRights[0]) {
                case "GAC_COMPARES":
                    requiredPatreonLevel = "Cherry";
                    break;
                case "MODS_GRAND_IVORY":
                case "MODS_ANALYSIS":
                case "MODS_LOADOUTS":
                case "MODS_PLAYGROUND":
                case "MODS_UNEQUIPPED":
                case "GAC_HISTORY":
                    requiredPatreonLevel = "Chile";
                    break;
                case "TERRITORY_BATTLES":
                case "TERRITORY_WARS":
                    requiredPatreonLevel = "Habanero";
                    break;
            }
        }
        if (requiredPatreonLevel !== null) {
            if (this.props.user) {
                if (this.props.user.shadow && this.props.requiredApiRights && this.props.requiredApiRights.length === 1 && this.props.requiredApiRights[0] === "TERRITORY_WARS") {
                    return <div className={styles.centeredNotice}><h2>Access to TW is not allowed from shared logins</h2></div>
                } else {
                    return <div className={styles.centeredNotice}><h2>This feature requires a {requiredPatreonLevel} Patreon level. <br/>
                        Your Patreon level is currently {this.props.user.patreonLevelString}. If this is not correct,
                        please run /identify in discord in a bot channel.<br/>
                        {requiredPatreonLevel === "Habanero" && <>If you have 4 or more guild members at Chile or above,
                            contact Hotsauce to upgrade your guild to Habanero status<br/></>}
                        If your Patreon payment failed to automatically renew, please try disconnecting discord from
                        Patreon and reconnecting it, then running /identify<br/>
                        Visit <a href="https://www.patreon.com/hotutils/membership">Patreon</a> to upgrade</h2></div>
                }
            } else {
                return <div className={styles.centeredNotice}><h2>This feature requires a {requiredPatreonLevel} Patreon level.<br/>
                    Visit <a href="https://www.patreon.com/hotutils/membership">Patreon</a> to upgrade</h2></div>
            }
        } else {
            return <div className={styles.centeredNotice}><h2>This feature requires a higher Patreon level than you currently are subscribed to.<br/>
                Visit <a href="https://www.patreon.com/hotutils/membership">Patreon</a> to upgrade</h2></div>
        }
    }

    private creditsDisplayValue(currency: number): string {
        if (currency >= 1000000000) {
            return (currency / 1000000000).toFixed(1) + "B";
        } else if (currency >= 1000000) {
            return (currency / 1000000).toFixed(1) + "M";
        } else {
            return (currency / 1000).toFixed(0) + "K";
        }
    }

    private getAccountMenu(player: PlayerData) {

        return (
            <Menu className={`${styles.menu} hu-account-menu`}>
                <Menu.Item key={1} className={styles['item-btn']}>
                    <Link to={`/player/profile`}>
                        <FontAwesomeIcon icon={faUser} title={i18next.t("common:header.account_profile")}
                                         className={styles.icon}/>
                        <span>{i18next.t("common:header.account_profile")}</span>
                    </Link>
                </Menu.Item>
                <Menu.Item key={2} className={styles['item-btn']}>
                    <Link to={`/accountselect`} onClick={() => this.onChangeAccount()}>
                        <FontAwesomeIcon icon={faUsers} title={i18next.t("common:header.account_select")}
                                         className={styles.icon}/>
                        <span>{i18next.t("common:header.account_select")}</span>
                    </Link>
                </Menu.Item>
                {player.gameDataAgeUtc && <Menu.Divider/>}
                {player.gameDataAgeUtc && <Menu.Item key={3} className={styles.item}
                                                     title={`Unequipped mods: ${player.mods.filter(mod => mod.equippedCharacter == null).length}/500`}>
                    <FontAwesomeIcon icon={faJournalWhills} className={styles.icon}/>
                    Mods: {player.mods.filter(mod => mod.equippedCharacter == null).length + "/500"}
                </Menu.Item>}
                {player.gameDataAgeUtc && <Menu.Item key={4} className={styles.item}
                                                     title={`Credits: ${this.creditsDisplayValue(player.credits)}`}>
                    <img className={styles.image} src="https://api.hotutils.com/images/currency/currency_credits.png"
                         alt={'Credits'} title={'Credits'}/>
                    Credits: {this.creditsDisplayValue(player.credits)}
                </Menu.Item>}
                {player.gameDataAgeUtc && <Menu.Item key={5} className={styles.item}
                                                     title={`Credits: ${this.creditsDisplayValue(player.shipCredits)}`}>
                    <img className={styles.image} src="https://api.hotutils.com/images/currency/currency_ship.png"
                         alt={'Ship Credits'} title={'Ship Credits'}/>
                    Ship Credits: {this.creditsDisplayValue(player.shipCredits)}
                </Menu.Item>}
                {player.gameDataAgeUtc && <Menu.Item key={6} className={styles.item}
                                                     title={`Credits: ${this.creditsDisplayValue(player.crystals)}`}>
                    <img className={styles.image} src="https://api.hotutils.com/images/currency/currency_crystals.png"
                         alt={'Crystals'} title={'Crystals'}/>
                    Crystals: {this.creditsDisplayValue(player.crystals)}
                </Menu.Item>}
                <Menu.Divider/>
                <Menu.Item key={8} className={`${styles.danger} ${styles['item-btn']}`}>
                    {/* Land on the landing page, NOT /login — signing out is not a prompt to sign back in, and
                        "/" is a plain Route (no PrivateRoute guard) so it stays put. /login remains registered
                        and is still where PrivateRoute sends an unauthenticated visitor. */}
                    <Link to="/" onClick={() => this.onLogout()} title={i18next.t("common:header.logout")}>
                            <FontAwesomeIcon icon={faSignOut} className={styles.icon}/>
                            <span>{i18next.t("common:header.logout")}</span>
                    </Link>
                </Menu.Item>
            </Menu>
        );
    }

    // The alternatives to the default Discord login. Discord itself is deliberately NOT repeated here — it is
    // the button, so a row for it would be a second target for the same action.
    private getLoginMenu() {
        return (
            <Menu className={`${styles.menu} hu-account-menu`}>
                <Menu.Item key="ea" className={styles['item-btn']}>
                    <a href="/eaconnect">
                        <FontAwesomeIcon icon={faJediOrder} className={styles.icon}/>
                        <span>{i18next.t("common:login.login_with_eaconnect")}</span>
                    </a>
                </Menu.Item>
            </Menu>
        );
    }

    private renderUserBanner(): React.ReactNode {
        // Logged out is a REAL state on public pages (the landing, /players/v2, /guilds/v2) — offer the
        // login entry point the design asks for, top right. The old behavior here was a spinner that never
        // resolved, which read as a broken page.
        // Discord is the default action and a real <a>, so middle-click / open-in-new-tab work on it; the
        // chevron half is the only part that opens the menu.
        if (!this.props.user || !this.props.user.currentPlayer)
            return <div className={styles.userArea}>
                <div className={styles.loginSplit}>
                    <a href={generateLink()} className={`${styles.dropdown} ${styles.loginPrimary}`}
                       title={i18next.t("common:login.login_with_discord")}>
                        <FontAwesomeIcon icon={faDiscord} className={styles.icon}/>
                        <span>{i18next.t("common:login.login_with_discord")}</span>
                    </a>
                    <Dropdown overlay={this.getLoginMenu()} trigger={['click']} arrow={true} placement="bottomRight">
                        <Button type={"link"} className={`${styles.dropdown} ${styles.loginMore}`}
                                aria-label={i18next.t("common:header.more_login_options")}>
                            <FontAwesomeIcon icon={faChevronDown} className={styles.arrow}/>
                        </Button>
                    </Dropdown>
                </div>
            </div>;
        if (!this.props.gameData)
            return <LoadingSpinner size={"small"} spinning={true}/>;

        return <div className={styles.userArea}>
                {/*Age account*/}
                <div className={styles.dataAge}>
                    {this.props.user.currentPlayer.gameDataAgeUtc && <FontAwesomeIcon icon={faCircleCheck} className={styles.syncIcon}/>}
                    {this.props.user.currentPlayer.gameDataAgeUtc ? (!this.condensed ? ("Data synced " + moment.utc(this.props.user.currentPlayer.gameDataAgeUtc).fromNow()) : ("Upd " + moment.utc(this.props.user.currentPlayer.gameDataAgeUtc).fromNow())) : "No Game Data"}
                </div>
                {/*Refresh button*/}
                {this.props.user.currentPlayer.hasConnection && <div className={styles.refresh} onClick={() => this.reload()}>
                    <FontAwesomeIcon icon={faSync} spin={this.reloading} />
                </div>}

                {/*Mods*/}
                {this.props.user.currentPlayer.gameDataAgeUtc && <div className={styles.mods} title={`Unequipped mods: ${this.props.user.currentPlayer.mods.filter(mod => mod.equippedCharacter == null).length}/500`}>
                    <div className={styles.image} />
                    {this.props.user.currentPlayer.mods.filter(mod => mod.equippedCharacter == null).length + "/500"}
                </div>}
                {/*Credits*/}
                {this.props.user.currentPlayer.gameDataAgeUtc && <div className={styles.credits}>
                    <img className={styles.image} src="https://api.hotutils.com/images/currency/currency_credits.png" alt={'Credits'} title={'Credits'} />
                    {this.props.user.currentPlayer.creditsDisplayValue}
                </div>}
                <Divider className={styles.divider} type="vertical" />
                <Dropdown overlay={this.getAccountMenu(this.props.user.currentPlayer)} trigger={['click']} className={styles.dropdown} arrow={true} placement="bottomCenter">
                    <Button type={"link"}>
                        {!this.condensed && <Avatar className={styles.avatar} size="large" >
                            <FontAwesomeIcon icon={faJediOrder} size={'2x'} className={styles.icon} />
                        </Avatar>}
                        <span className={styles.title}> {this.props.user.currentPlayer.name}</span>
                        <FontAwesomeIcon icon={faChevronDown} className={styles.arrow} />
                    </Button>
                </Dropdown>
            </div>;

    }

    private onChangeAccount() {
        if (this.props.cookies) {
            return BaseAPI.clearCache();
        }
    }

    private onLogout(): void {
        if (this.props.user)
            this.props.user.logout();
        if (!this.props.cookies)
            return;
        if (this.props.cookies.get("hotUtilsSession"))
            this.props.cookies.remove("hotUtilsSession");
        if (this.props.cookies.get("hotUtilsAllyCode"))
            this.props.cookies.remove("hotUtilsAllyCode");
    }

    private async reload(): Promise<void> {
        runInAction(() => this.reloading = true);
        let waitDlg = Modal.info({
            maskClosable: false,
            okButtonProps: {disabled: true, style: {display: 'none'}},
            title: "Please wait, refreshing data from game server",
            content: <Spin size="large"/>
        });
        try {
            await BaseAPI.fetchAllCurrentPlayerData(this.props.user!, this.props.gameData!, false, true);

            // null out TB data in case they loaded it and moved to another component then did refresh
            runInAction(() => {
                this.props.user!.currentPlayer!.templateGroups = null;
                this.props.user!.currentPlayer!.territoryBattleData = null;
                this.props.user!.currentPlayer!.tbHistorySelections = [];
            });
            if (this.props.extraRefresh !== undefined) {
                await this.props.extraRefresh();
            }
            runInAction(() => this.reloading = false);
            waitDlg.destroy();
        } catch (err: any) {
            waitDlg.destroy();
            Modal.error({
                title: i18next.t("common:ui.error_dlg_title"),
                content: <span>{err.errorMessage}</span>,
                maskClosable: false
            });
        }
    }
}
